import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Key, Plus, Copy, Check, Trash2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ApiKey {
  id: string;
  name: string;
  description: string | null;
  keyPrefix: string;
  createdBy: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  scopes: string[];
  createdAt: string;
}

interface NewKeyResult extends ApiKey {
  fullKey: string;
}

const AVAILABLE_SCOPES = [
  { id: "vouchers", label: "Vouchers", description: "Generate prepaid voucher codes (partner integration)" },
  { id: "read:content", label: "Read Content", description: "Questions, topics, grades, reading materials" },
  { id: "write:submissions", label: "Write Submissions", description: "Submit quiz results and progress" },
  { id: "read:users", label: "Read Users", description: "User profiles and progress data" },
  { id: "write:users", label: "Write Users", description: "Create or update user data" },
  { id: "read:analytics", label: "Read Analytics", description: "Platform stats and analytics" },
  { id: "mobile:full", label: "Mobile Access", description: "Full access to the mobile API endpoints" },
  { id: "admin", label: "Admin", description: "Full administrative access (use with caution)" },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button variant="outline" size="sm" onClick={copy}>
      {copied ? <Check className="h-4 w-4 mr-1.5 text-green-500" /> : <Copy className="h-4 w-4 mr-1.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function ScopesBadges({ scopes }: { scopes: string[] }) {
  if (!scopes?.length) return <span className="text-xs text-muted-foreground">No scopes</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((s) => (
        <Badge key={s} variant="secondary" className="text-xs font-normal">
          {s}
        </Badge>
      ))}
    </div>
  );
}

export default function AdminApiKeys() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<NewKeyResult | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [form, setForm] = useState({ name: "", description: "", scopes: [] as string[] });

  const { data: keys = [], isLoading } = useQuery<ApiKey[]>({
    queryKey: ["/api/admin/api-keys"],
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/api-keys", {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        scopes: form.scopes,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create key");
      }
      return res.json() as Promise<NewKeyResult>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
      setCreateOpen(false);
      setForm({ name: "", description: "", scopes: [] });
      setNewKeyResult(data);
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/api-keys/${id}`, { isActive });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
    },
    onError: () => {
      toast({ title: "Failed to update key", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/admin/api-keys/${id}`);
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-keys"] });
      setDeleteId(null);
      toast({ title: "API key revoked." });
    },
    onError: () => {
      toast({ title: "Failed to revoke key", variant: "destructive" });
    },
  });

  const toggleScope = (scopeId: string) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scopeId)
        ? prev.scopes.filter((s) => s !== scopeId)
        : [...prev.scopes, scopeId],
    }));
  };

  const activeCount = keys.filter((k) => k.isActive).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate and manage API keys for external integrations and mobile app access.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Generate New Key
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5">
            <p className="text-sm text-muted-foreground">Total Keys</p>
            <p className="text-3xl font-bold mt-1">{keys.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-3xl font-bold mt-1 text-green-600 dark:text-green-400">{activeCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-sm text-muted-foreground">Inactive</p>
            <p className="text-3xl font-bold mt-1 text-muted-foreground">{keys.length - activeCount}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Key className="h-4 w-4" />
            Keys
          </CardTitle>
          <CardDescription className="text-xs">
            The full key is shown only once when created. Treat API keys like passwords. Never commit them to source code.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !keys.length ? (
            <div className="py-16 text-center">
              <Key className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No API keys yet. Generate one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} className={!k.isActive ? "opacity-50" : ""}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{k.name}</p>
                        {k.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{k.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{k.keyPrefix}</code>
                    </TableCell>
                    <TableCell>
                      <ScopesBadges scopes={k.scopes} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {k.lastUsedAt
                        ? formatDistanceToNow(new Date(k.lastUsedAt), { addSuffix: true })
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(k.createdAt), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={k.isActive}
                        onCheckedChange={(v) => toggleMutation.mutate({ id: k.id, isActive: v })}
                        disabled={toggleMutation.isPending}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setDeleteId(k.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Generate New API Key
            </DialogTitle>
            <DialogDescription>
              The full key will only be shown once. Copy it immediately after creating.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input
                placeholder="e.g. Supreme Ventures Limited, GiftMe, Mobile App"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Textarea
                placeholder="What will this key be used for?"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                className="resize-none text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label>Scopes</Label>
              <p className="text-xs text-muted-foreground">Select what this key is allowed to access.</p>
              <div className="space-y-2 rounded-md border p-3">
                {AVAILABLE_SCOPES.map((scope) => (
                  <div key={scope.id} className="flex items-start gap-3">
                    <Checkbox
                      id={scope.id}
                      checked={form.scopes.includes(scope.id)}
                      onCheckedChange={() => toggleScope(scope.id)}
                      className="mt-0.5"
                    />
                    <label htmlFor={scope.id} className="cursor-pointer">
                      <p className="text-sm font-medium leading-none">{scope.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{scope.description}</p>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!form.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Key className="h-4 w-4 mr-1.5" />
              )}
              Generate Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Newly Created Key Dialog */}
      <Dialog open={!!newKeyResult} onOpenChange={(open) => { if (!open) setNewKeyResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <ShieldCheck className="h-5 w-5" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy your key now. For security, it will not be shown again.
            </DialogDescription>
          </DialogHeader>

          {newKeyResult && (
            <div className="space-y-4">
              <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Store this key somewhere safe. You won't be able to see it again after closing this dialog.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Your API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-muted px-3 py-2 rounded-md break-all">
                    {newKeyResult.fullKey}
                  </code>
                  <CopyButton text={newKeyResult.fullKey} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Name</p>
                  <p className="font-medium">{newKeyResult.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Scopes</p>
                  <ScopesBadges scopes={newKeyResult.scopes} />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={() => setNewKeyResult(null)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any application using this key will immediately lose access. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
